import * as React from "react";
import { css } from "@emotion/core";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  FormGroup,
  InputGroup,
  MenuItem,
  Tag,
  Dialog,
  Classes,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
  UL,
  Icon,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { useSinkActions, useSinkState, useAuthState } from "context/";
import { useControlledInput } from "hooks/";
import FolderInfo from "components/common/FolderInfo";

const FolderSelect = Select.ofType<IFolder>();

const respondOrError = (response) => {
  if (response.status === 404) throw new Error("Not Found");
  return response.json();
};

const createSurvey = (params) =>
  fetch(`/api/v1/surveys`, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  }).then(respondOrError);

const CreateSurvey = ({ show, hide }) => {
  const { resink } = useSinkActions();
  const { sink } = useSinkState();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const {
    valid: $name,
    reset,
    ...name
  } = useControlledInput((v) => v !== "" && v.length >= 3);

  const [creatingSurvey, setCreatingSurvey] = React.useState(false);

  const [selectedFolder, setSelectedFolder] = React.useState(null);
  const [folderQuery, setFolderQuery] = React.useState("");

  const selectedFolderName = selectedFolder
    ? sink.folders.find((folder) => folder.id === selectedFolder).label
    : "";

  const canCreate = $name && selectedFolder !== null;

  React.useEffect(() => {
    if (location.pathname.startsWith("/folders") && params.id) {
      setSelectedFolder(Number(params.id));
    } else {
      setSelectedFolder(null);
    }
  }, [location.pathname, params.id]);

  const handleSubmit = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();

    setCreatingSurvey(true);

    const survey = await createSurvey({
      label: name.value,
      folder_id: selectedFolder,
    });

    await resink();

    hide();
    setCreatingSurvey(false);

    navigate(`/surveys/${survey.id}/orders/new`);
  };

  return (
    <Dialog
      className="bp6-dark"
      icon="map-create"
      title="Create Survey"
      isOpen={show}
      onClose={hide}
    >
      <div className={Classes.DIALOG_BODY}>
        <FormGroup
          label="Survey Name"
          labelFor="survey-name"
          labelInfo="(required, minimum 3 characters)"
        >
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <InputGroup
              autoFocus
              id="survey-name"
              placeholder="Choose a Survey Name"
              {...name}
              css={css`
                flex: 1;
                margin-right: 1rem;
              `}
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  handleSubmit(event);
                }
              }}
            />
            <Popover
              interactionKind={PopoverInteractionKind.HOVER}
              position={PopoverPosition.RIGHT}
              content={
                <UL
                  css={css`
                    max-width: 512px;
                    margin: 0;
                    padding-right: 1rem;
                    padding-top: 1rem;
                    padding-bottom: 1rem;
                  `}
                >
                  <li>
                    A survey name is required for each survey and is used to
                    describe and search for the survey (along with survey date,
                    tags, folders, location and sites).
                  </li>
                  <li>
                    It can be any combination of text and characters at least 3
                    characters long.
                  </li>
                  <li>
                    Oftentimes it is valuable to describe the intent of the
                    survey and/or place (or site) it took place in the survey
                    name.
                  </li>
                  <li>
                    Whereas “Sites” are formally defined and must have a unique
                    name and a defined location, survey names can be re-used.
                  </li>
                  <li>
                    Examples: “October inventory survey - Mill”, “Drainage
                    investigation - Yard west”, “Brushing survey - Block B” and
                    “CPL_40321_Bob - Residuals”.
                  </li>
                  <li>Survey names appear on generated survey reports.</li>
                  <li>
                    Survey names can be modified after survey creation by users
                    with admin or member permissions.
                  </li>
                </UL>
              }
            >
              <Icon
                css={css`
                  margin-left: 1rem;
                `}
                icon="help"
              />
            </Popover>
          </div>
        </FormGroup>
        <FormGroup
          label="Folder"
          labelFor="survey-folder"
          labelInfo="(required)"
        >
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            {selectedFolder ? (
              <Tag
                size="large"
                icon="folder-close"
                onRemove={() => setSelectedFolder(null)}
              >
                {selectedFolderName}
              </Tag>
            ) : (
              <FolderSelect
                items={sink.folders.filter((folder) =>
                  folder.label
                    .toLowerCase()
                    .startsWith(folderQuery.toLowerCase())
                )}
                query={folderQuery}
                onQueryChange={(query, event) => {
                  setFolderQuery(query);
                }}
                itemRenderer={(item) => (
                  <MenuItem
                    css={css`
                      min-width: 260px;
                    `}
                    key={item.label}
                    icon="folder-close"
                    text={item.label}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedFolder(item.id);
                    }}
                  />
                )}
                onItemSelect={() => null}
              >
                <Button text="Choose Folder" endIcon="double-caret-vertical" />
              </FolderSelect>
            )}
            <FolderInfo />
          </div>
        </FormGroup>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={hide} disabled={creatingSurvey}>
            Cancel
          </Button>
          <Button
            disabled={canCreate === false}
            loading={creatingSurvey}
            intent="primary"
            onClick={handleSubmit}
          >
            Create Survey
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateSurvey;
